import React from "react";
import { Link } from "react-router-dom";
import { LegalLayout, LegalSection, LegalList } from "@/components/site/LegalLayout";

const PRIVACY_EMAIL = "privacy@psieconsulting.com";

const DATA_COLLECTED = [
  "Contact form submissions: your name, email address, company/facility, selected service of interest, and the message you send us.",
  "AI assistant conversations: the messages you type into our AI support chat and the assistant's responses, along with a randomly generated session identifier stored in your browser.",
  "Technical data: basic log and device information (such as IP address, browser type, and pages visited) generated automatically when you use the site.",
];

const DATA_USES = [
  "To respond to your inquiries and provide the consulting services you request.",
  "To operate, maintain, and improve our AI support assistant and website.",
  "To detect, prevent, and address technical issues, fraud, or abuse.",
  "To comply with legal obligations.",
];

const THIRD_PARTIES = [
  "AI processing: OpenAI (via the Emergent platform) — processes chat messages to generate responses.",
  "Hosting & database: our cloud hosting and managed database providers store contact submissions and chat history.",
  "These providers are bound by their own privacy and security terms and process data only as needed to provide their services.",
];

const RETENTION_RIGHTS = [
  "AI chat: you can delete your current conversation instantly using the “Clear chat” (trash) button in the chat window, which permanently removes that conversation from our servers.",
  `Contact submissions and other data: email us at ${PRIVACY_EMAIL} to request deletion, and we will remove your data promptly.`,
];

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 2026">
      <p className="text-[#5C6C80]">
        This Privacy Policy explains how Process Safety &amp; Integrity Engineering Consultants
        (&ldquo;PSIEC&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) collects, uses, shares, and
        protects information when you use this website, our contact form, and our AI support
        assistant. By using this site, you agree to the practices described here.
      </p>

      <LegalSection heading="1. We Collect User Data">
        <p>
          <strong className="text-[#04162E]">Yes — we collect user data.</strong> We collect the
          following information:
        </p>
        <LegalList items={DATA_COLLECTED} />
      </LegalSection>

      <LegalSection heading="2. How We Use Your Data">
        <LegalList items={DATA_USES} />
        <p>We do not sell your personal data to third parties.</p>
      </LegalSection>

      <LegalSection heading="3. Use of Artificial Intelligence (AI)">
        <p>
          Our support assistant (&ldquo;Emmy&rdquo;) is powered by third-party artificial
          intelligence. When you send a message to the assistant,{" "}
          <strong className="text-[#04162E]">
            your message is transmitted to and processed by an AI provider
          </strong>{" "}
          (OpenAI, accessed via the Emergent platform) in order to generate a response. AI
          responses may be inaccurate or incomplete and must not be relied upon as professional
          engineering, safety, legal, or medical advice. Please do not enter sensitive personal
          or confidential information into the chat.
        </p>
      </LegalSection>

      <LegalSection heading="4. Third-Party Data Processors">
        <p>We share data with the following categories of third-party service providers who process it on our behalf:</p>
        <LegalList items={THIRD_PARTIES} />
      </LegalSection>

      <LegalSection heading="5. Data Retention & Deletion">
        <p>
          We retain contact submissions and chat history only as long as necessary for the
          purposes above. You have the right to request access to, correction of, or deletion of
          your personal data at any time.
        </p>
        <LegalList items={RETENTION_RIGHTS} />
      </LegalSection>

      <LegalSection heading="6. Cookies & Local Storage">
        <p>
          We use browser local storage to remember your AI chat session identifier so your
          conversation persists during your visit. We do not use advertising or cross-site
          tracking cookies. You can clear this data through your browser at any time.
        </p>
      </LegalSection>

      <LegalSection heading="7. Data Security">
        <p>
          We take reasonable technical and organizational measures to protect your data. Stored
          data is not made publicly accessible. However, no method of transmission or storage is
          completely secure, and we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection heading="8. Children's Privacy">
        <p>
          This site is intended for business and professional use and is not directed to
          children under 16. We do not knowingly collect personal data from children.
        </p>
      </LegalSection>

      <LegalSection heading="9. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be reflected
          by the &ldquo;Last updated&rdquo; date above.
        </p>
      </LegalSection>

      <LegalSection heading="10. Contact Us">
        <p>
          For privacy questions or data requests, contact us at{" "}
          <a href={`mailto:${PRIVACY_EMAIL}`} className="text-[#0B6B44] underline hover:text-[#04162E]">
            {PRIVACY_EMAIL}
          </a>{" "}
          or through our{" "}
          <Link to="/#contact" className="text-[#0B6B44] underline hover:text-[#04162E]">
            contact form
          </Link>
          . See also our{" "}
          <Link to="/terms" className="text-[#0B6B44] underline hover:text-[#04162E]">
            Terms of Service
          </Link>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
